/**
 * One thin, typed, fetch-based API client. It adds headers, unwraps the `{ data }` envelope and turns
 * failures into `ApiError`. It contains no business logic and does not know any endpoint.
 */
import { env } from '@/lib/env';
import { newCorrelationId } from '@/lib/ids';
import { API_ERROR_CODES, ApiError } from './errors';
import type { ApiMeta, ErrorEnvelope, Pagination } from './types';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientOptions {
  /** API origin. Defaults to `VITE_API_BASE_URL` (empty means same origin). */
  baseUrl?: string;
  /** Path prefix of the versioned API. */
  apiPrefix?: string;
  /** Returns the Bearer token to send, or null for none. Read on every request. */
  getToken?: () => string | null;
  /** Called after a 401 on a request that carried a token (not on token-less requests such as login). */
  onUnauthorized?: (error: ApiError) => void;
  /** Injectable for tests. */
  fetch?: typeof globalThis.fetch;
}

export interface RequestOptions {
  method?: HttpMethod;
  /** Any JSON-serialisable body, sent as `application/json`. */
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Sent as `Idempotency-Key`. Generate one per user intent and reuse it when retrying. */
  idempotencyKey?: string;
  /** Sent as `X-Correlation-ID`. Generated when omitted. */
  correlationId?: string;
  /** Sent as `Last-Event-ID` (event streams). */
  lastEventId?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Set false to send no Authorization header even when a token exists. */
  auth?: boolean;
}

export interface ApiResult<T> {
  data: T;
  meta?: ApiMeta;
  pagination?: Pagination;
  status: number;
  correlationId: string;
}

export type GetOptions = Omit<RequestOptions, 'method' | 'body'>;
export type MutationOptions = Omit<RequestOptions, 'method' | 'body'>;

export interface ApiClient {
  /** Full result: data, meta, pagination, status and correlation id. */
  send<T>(path: string, options?: RequestOptions): Promise<ApiResult<T>>;
  /** Just the unwrapped `data`. */
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  get<T>(path: string, options?: GetOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: MutationOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: MutationOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: MutationOptions): Promise<T>;
  delete<T>(path: string, options?: GetOptions): Promise<T>;
  /** Opens a GET response whose body is read as a stream (Server-Sent Events). Throws `ApiError` on non-2xx. */
  openStream(path: string, options?: GetOptions): Promise<Response>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : (error as Error)?.name === 'AbortError';
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? env.apiBaseUrl).replace(/\/+$/, '');
  const apiPrefix = options.apiPrefix ?? '/api/v1';
  const doFetch =
    options.fetch ?? ((...args: Parameters<typeof fetch>) => globalThis.fetch(...args));

  function buildUrl(path: string, query?: RequestOptions['query']): string {
    let url = `${baseUrl}${apiPrefix}${path.startsWith('/') ? path : `/${path}`}`;
    if (query) {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null) params.set(key, String(value));
      }
      const text = params.toString();
      if (text) url += `?${text}`;
    }
    return url;
  }

  async function execute(
    path: string,
    request: RequestOptions,
    accept: string,
  ): Promise<{ response: Response; correlationId: string; sentToken: boolean }> {
    const correlationId = request.correlationId ?? newCorrelationId();
    const headers: Record<string, string> = {
      ...request.headers,
      Accept: accept,
      'X-Correlation-ID': correlationId,
    };

    const token = request.auth === false ? null : (options.getToken?.() ?? null);
    if (token) headers.Authorization = `Bearer ${token}`;
    if (request.idempotencyKey) headers['Idempotency-Key'] = request.idempotencyKey;
    if (request.lastEventId) headers['Last-Event-ID'] = request.lastEventId;

    let body: string | undefined;
    if (request.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(request.body);
    }

    try {
      const response = await doFetch(buildUrl(path, request.query), {
        method: request.method ?? 'GET',
        headers,
        body,
        signal: request.signal,
      });
      return { response, correlationId, sentToken: token !== null };
    } catch (error) {
      if (isAbort(error)) throw error;
      throw new ApiError({
        status: 0,
        code: API_ERROR_CODES.NETWORK_ERROR,
        message: 'The request could not be completed. Check the network connection.',
        correlationId,
      });
    }
  }

  async function failure(response: Response, correlationId: string): Promise<ApiError> {
    const effectiveId = response.headers.get('x-correlation-id') ?? correlationId;
    let parsed: unknown;
    try {
      parsed = JSON.parse(await response.text());
    } catch {
      parsed = undefined;
    }

    const envelope = isRecord(parsed) ? (parsed as Partial<ErrorEnvelope>).error : undefined;
    if (isRecord(envelope) && typeof envelope.code === 'string') {
      return new ApiError({
        status: response.status,
        code: envelope.code,
        message: typeof envelope.message === 'string' ? envelope.message : response.statusText,
        details: isRecord(envelope.details) ? envelope.details : undefined,
        correlationId:
          typeof envelope.correlation_id === 'string' ? envelope.correlation_id : effectiveId,
        timestamp: typeof envelope.timestamp === 'string' ? envelope.timestamp : undefined,
      });
    }
    return new ApiError({
      status: response.status,
      code: `HTTP_${response.status}`,
      message: response.statusText || 'The request failed.',
      correlationId: effectiveId,
    });
  }

  async function send<T>(path: string, request: RequestOptions = {}): Promise<ApiResult<T>> {
    const { response, correlationId, sentToken } = await execute(path, request, 'application/json');

    if (!response.ok) {
      const error = await failure(response, correlationId);
      if (response.status === 401 && sentToken) options.onUnauthorized?.(error);
      throw error;
    }

    const effectiveId = response.headers.get('x-correlation-id') ?? correlationId;
    const text = response.status === 204 ? '' : await response.text();
    if (text === '') {
      return { data: undefined as T, status: response.status, correlationId: effectiveId };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
    if (!isRecord(parsed) || !('data' in parsed)) {
      throw new ApiError({
        status: response.status,
        code: API_ERROR_CODES.INVALID_RESPONSE,
        message: 'The server sent a response this app cannot read.',
        correlationId: effectiveId,
      });
    }

    const envelope = parsed as { data: T; meta?: ApiMeta; pagination?: Pagination };
    return {
      data: envelope.data,
      meta: envelope.meta,
      pagination: envelope.pagination,
      status: response.status,
      correlationId: envelope.meta?.correlation_id ?? effectiveId,
    };
  }

  async function request<T>(path: string, requestOptions?: RequestOptions): Promise<T> {
    return (await send<T>(path, requestOptions)).data;
  }

  return {
    send,
    request,
    get: (path, o) => request(path, { ...o, method: 'GET' }),
    post: (path, body, o) => request(path, { ...o, method: 'POST', body }),
    patch: (path, body, o) => request(path, { ...o, method: 'PATCH', body }),
    put: (path, body, o) => request(path, { ...o, method: 'PUT', body }),
    delete: (path, o) => request(path, { ...o, method: 'DELETE' }),
    async openStream(path, o = {}) {
      const { response, correlationId, sentToken } = await execute(
        path,
        { ...o, method: 'GET' },
        'text/event-stream',
      );
      if (!response.ok) {
        const error = await failure(response, correlationId);
        if (response.status === 401 && sentToken) options.onUnauthorized?.(error);
        throw error;
      }
      return response;
    },
  };
}
